import { useState } from "react";
import { formatDateOnly } from "@/lib/date-only";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { apiGet, apiPatch, apiPost } from "@/lib/api";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  ChevronLeft,
  CheckCircle,
  XCircle,
  MinusCircle,
  Loader2,
} from "lucide-react";

export const Route = createFileRoute(
  "/_authenticated/inspections/$inspectionId"
)({
  component: InspectionDetailPage,
});

interface InspectionItem {
  id: string;
  templateItemId: string;
  label: string | null;
  description: string | null;
  isRequired: boolean | null;
  sortOrder: number | null;
  result: string | null;
  notes: string | null;
  photoFileId: string | null;
}

function InspectionDetailPage() {
  const { inspectionId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [itemUpdates, setItemUpdates] = useState<
    Record<string, { result: string; notes: string }>
  >({});

  const { data, isLoading } = useQuery({
    queryKey: ["inspection", inspectionId],
    queryFn: () =>
      apiGet<{ data: any }>(`/api/inspections/${inspectionId}`),
  });

  const inspection = data?.data;
  const items: InspectionItem[] = inspection?.items || [];

  const saveMutation = useMutation({
    mutationFn: () => {
      const changedItems = Object.entries(itemUpdates).map(
        ([templateItemId, update]) => ({
          templateItemId,
          result: update.result,
          notes: update.notes || undefined,
        })
      );
      return apiPatch(`/api/inspections/${inspectionId}`, {
        items: changedItems,
      });
    },
    onSuccess: () => {
      toast.success("Progress saved");
      queryClient.invalidateQueries({
        queryKey: ["inspection", inspectionId],
      });
      setItemUpdates({});
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const completeMutation = useMutation({
    mutationFn: () =>
      apiPost<{ success: boolean; overallResult: string }>(
        `/api/inspections/${inspectionId}/complete`
      ),
    onSuccess: (res) => {
      toast.success(
        `Inspection completed: ${(res as any).overallResult === "pass" ? "PASS" : "FAIL"}`
      );
      queryClient.invalidateQueries({
        queryKey: ["inspection", inspectionId],
      });
      queryClient.invalidateQueries({ queryKey: ["inspections"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const getItemResult = (item: InspectionItem): string | null => {
    return itemUpdates[item.templateItemId]?.result ?? item.result;
  };

  const getItemNotes = (item: InspectionItem): string => {
    return itemUpdates[item.templateItemId]?.notes ?? item.notes ?? "";
  };

  const updateItem = (
    templateItemId: string,
    field: "result" | "notes",
    value: string
  ) => {
    setItemUpdates((prev) => {
      const existing = prev[templateItemId] ?? { result: "", notes: "" };
      return {
        ...prev,
        [templateItemId]: { ...existing, [field]: value },
      };
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-4 max-w-lg mx-auto pt-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (!inspection) {
    return (
      <div className="text-center pt-12">
        <p className="text-muted-foreground">Inspection not found</p>
      </div>
    );
  }

  const isCompleted = inspection.status === "completed";
  const allItemsAnswered = items.every(
    (i) => getItemResult(i) !== null && getItemResult(i) !== ""
  );
  const hasChanges = Object.keys(itemUpdates).length > 0;

  return (
    <div className="space-y-4 max-w-lg mx-auto pb-20">
      <div className="flex items-center gap-2 pt-2">
        <Button
          variant="ghost"
          size="sm"
          className="p-1"
          onClick={() => navigate({ to: "/inspections" })}
        >
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-xl font-bold text-text-primary truncate">
          Inspection
        </h1>
      </div>

      <Card>
        <CardContent className="py-3 px-4 space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            {isCompleted ? (
              inspection.overallResult === "pass" ? (
                <Badge className="bg-green-600 text-white gap-1">
                  <CheckCircle className="h-3 w-3" /> Pass
                </Badge>
              ) : (
                <Badge className="bg-red-600 text-white gap-1">
                  <XCircle className="h-3 w-3" /> Fail
                </Badge>
              )
            ) : (
              <Badge variant="outline" className="border-yellow-300 text-yellow-700">
                In Progress
              </Badge>
            )}
            <span className="text-xs text-muted-foreground">
              {formatDateOnly(inspection.date)}
            </span>
          </div>
          {inspection.notes && (
            <p className="text-sm text-muted-foreground">{inspection.notes}</p>
          )}
        </CardContent>
      </Card>

      {/* Inspection items */}
      <div className="space-y-2">
        <h3 className="text-sm font-semibold">
          Checklist ({items.filter((i) => getItemResult(i)).length}/{items.length})
        </h3>

        {items.map((item) => {
          const result = getItemResult(item);
          return (
            <Card key={item.id}>
              <CardContent className="py-3 px-4 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <p className="text-sm font-medium">
                      {item.label}
                      {item.isRequired && (
                        <span className="text-destructive ml-1">*</span>
                      )}
                    </p>
                    {item.description && (
                      <p className="text-xs text-muted-foreground">
                        {item.description}
                      </p>
                    )}
                  </div>
                </div>

                {!isCompleted ? (
                  <>
                    <div className="flex gap-2">
                      {["pass", "fail", "na"].map((val) => (
                        <button
                          key={val}
                          type="button"
                          onClick={() =>
                            updateItem(item.templateItemId, "result", val)
                          }
                          className={cn(
                            "flex-1 py-2 px-3 rounded-md text-xs font-medium border transition-colors",
                            result === val
                              ? val === "pass"
                                ? "bg-green-100 border-green-400 text-green-800"
                                : val === "fail"
                                  ? "bg-red-100 border-red-400 text-red-800"
                                  : "bg-gray-100 border-gray-400 text-gray-800"
                              : "bg-white border-border hover:bg-muted/50"
                          )}
                        >
                          {val === "pass" && (
                            <CheckCircle className="h-3 w-3 inline mr-1" />
                          )}
                          {val === "fail" && (
                            <XCircle className="h-3 w-3 inline mr-1" />
                          )}
                          {val === "na" && (
                            <MinusCircle className="h-3 w-3 inline mr-1" />
                          )}
                          {val.toUpperCase()}
                        </button>
                      ))}
                    </div>
                    {result === "fail" && (
                      <textarea
                        value={getItemNotes(item)}
                        onChange={(e) =>
                          updateItem(
                            item.templateItemId,
                            "notes",
                            e.target.value
                          )
                        }
                        placeholder="Notes on failure..."
                        rows={2}
                        className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      />
                    )}
                  </>
                ) : (
                  <div className="flex items-center gap-2">
                    {result === "pass" && (
                      <Badge className="bg-green-100 text-green-800">Pass</Badge>
                    )}
                    {result === "fail" && (
                      <Badge className="bg-red-100 text-red-800">Fail</Badge>
                    )}
                    {result === "na" && (
                      <Badge variant="secondary">N/A</Badge>
                    )}
                    {item.notes && (
                      <span className="text-xs text-muted-foreground">
                        {item.notes}
                      </span>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Actions */}
      {!isCompleted && (
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="flex-1"
            disabled={!hasChanges || saveMutation.isPending}
            onClick={() => saveMutation.mutate()}
          >
            {saveMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              "Save Progress"
            )}
          </Button>
          <Button
            className="flex-1 bg-paris-red hover:bg-paris-red-dark"
            disabled={!allItemsAnswered || completeMutation.isPending}
            onClick={() => completeMutation.mutate()}
          >
            {completeMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              "Complete"
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
